import { action, observable, computed, makeObservable } from 'mobx';
import { apiService } from '../services/api.service';
import { history } from 'fn-common-ui';
import { utilsService } from '../services/utils.service';
import _ from 'lodash';

class MyAppsStore {

	constructor() {
		makeObservable(this);
	}

	// ACTIONS
	@action appRowSelected (app) {
		history.push(utilsService.generateAppPageURI(app, 'view'));
	}
	
	@action getMyApps() {
		const success = (res) => {
			this.myApps = res.data;
			normalizeLastActionDate(this.myApps.unReleasedApps);
			this.apps.released = this.myApps.releasedApps;
			this.apps.unReleased = this.myApps.unReleasedApps;
			this.hideSearchBox = (res.data.releasedApps.length < 1 && res.data.unReleasedApps.length < 1);
			this.isLoading = false;
		};

		const fail = (err) => {
			this.isLoading = false;
			throw err;
		};

		this.isLoading = true;
		apiService.getMyApps().then(success, fail);
	}

	@action toggleSearchBox(){
		this.hideSearchBox = !this.hideSearchBox || document.getElementsByClassName('joyride-overlay').length;
	}

	@action searchApps() {
		this.apps.released = searchApps(this.myApps.releasedApps, this.searchQuery);
		this.apps.unReleased = searchApps(this.myApps.unReleasedApps, this.searchQuery);
	}

	@action handleInput(val) {
		this.searchQuery = val;
	}

	@action handleJoyrideStep(stepInfo) {
		if(stepInfo.step && stepInfo.step.selector === '.walkthrough-search-and-filter' && stepInfo.type.indexOf('beacon') < 0) {
			this.forceOpenSearch = true;
		} else {
			this.forceOpenSearch = false;
		}
	}

	@action resetApps(){
		this.myApps = {releasedApps: [], unReleasedApps: []};
		this.apps = {
			released: [],
			unReleased: []
		};
	}

	@action clearSearchQuery(){
		this.searchQuery = '';
		this.searchApps();
	}

	@action toggleFilter(name, list, bool) {
		this.filters[list][name] = bool;
	}

	@action toggleSort({list, property, title}) {
		if (property !== this.activeSort[list].property) {
			this.activeSort[list].property = property;
			this.activeSort[list].title = title;
			this.activeSort[list].direction = 'desc';
		} else {
			this.activeSort[list].direction = reverseSortDirection(this.activeSort[list].direction);
		}
	}

	@action resetFilters() {
		this.filters = {
			'unReleased': {
				'DRAFT': true,
				'UNDER_REVIEW': true,
				'APPROVED': true,
				'REJECTED': true,
				'WITHDRAWN': false,
			},
			'released': {
				'LIVE': true,
				'INACTIVE': false
			}
		};
		this.searchQuery = '';
		this.searchApps();
	}


	// COMPUTEDS
	@computed get released() {
		let apps = filterApps(this.apps.released, this.filters.released);
		return _.orderBy(apps, app => lowerCaseSort(app, this.activeSort.released.property), this.activeSort.released.direction)
	}
	@computed get unReleased() {
		let apps = filterApps(this.apps.unReleased, this.filters.unReleased);
		return _.orderBy(apps, app => lowerCaseSort(app, this.activeSort.unReleased.property), this.activeSort.unReleased.direction)
	}

	@computed get numAppsWithStatus() {
		// counts the number of items that have each status. Used in if we should show certain check boxes.
		let statuses = {'DRAFT': 0, 'UNDER_REVIEW': 0,'REJECTED': 0, 'WITHDRAWN': 0, 'LIVE': 0, 'INACTIVE': 0, 'APPROVED': 0, 'UNAVAILABLE' : 0};
		this.apps.released.forEach((app) =>{
			let status = app.custom_metadata.status || 'UNAVAILABLE';
			statuses[status]++
		});
		this.apps.unReleased.forEach((app) =>{
			let status = app.custom_metadata.status || 'UNAVAILABLE';
			statuses[status]++
		});
		return statuses;
	}

	// OBSERVABLES
	@observable isLoading = false;

	@observable myApps = {releasedApps: [], unReleasedApps: []};
	@observable apps = {
		released: [],
		unReleased: []
	};
	@observable activeSort = {
		released: {
			property: 'custom_metadata.release_date',
			title: 'Release Date',
			direction: 'desc'
		},
		unReleased: {
			property: 'custom_metadata.lastActionDate',
			title: 'Last Update',
			direction: 'desc'
		}
	};

	@observable searchQuery = '';
	@observable filters = {
		'unReleased': {
			'DRAFT': true,
			'UNDER_REVIEW': true,
			'APPROVED': true,
			'REJECTED': true,
			'WITHDRAWN': false,
		},
		'released': {
			'LIVE': true,
			'INACTIVE': false
		}
	};

	@observable hideSearchBox = false;
	@observable forceOpenSearch = false;

}

const searchApps = (apps, query) => {
	if (query === '') {
		return apps;
	} else {
		return _.filter(apps, (app) => app.app_name.toLowerCase().includes(query.toLowerCase()))
	}
};

const filterApps = (apps, filters) => {
	return _.filter(apps, (app) => filters[ (app.custom_metadata.status || 'UNAVAILABLE') ])
};

const lowerCaseSort = (app, sortString) => {
	let sortableKey = _.at(app, sortString)[0];
	if (typeof sortableKey === 'string') {
		return sortableKey.toLowerCase();
	} else if (!sortableKey) {
		//In case of undefined or empty values, return as empty strings so that these will sort together.
		return '';
	} else {
		return sortableKey;
	}

};


const normalizeLastActionDate = (appsArray) => {
	appsArray.forEach((app) => {
		let status = app.custom_metadata.status;
		let lastActionDate;
		switch(status) {
			case "DRAFT":
				lastActionDate = app.lastSavedDatetime;
				break;
			case "UNDER_REVIEW":
				lastActionDate = app.custom_metadata.submitted_date;
				break;
			case "APPROVED":
				lastActionDate = app.custom_metadata.approved_date;
				break;
			case "REJECTED":
				lastActionDate = app.custom_metadata.rejected_date;
				break;
			case "WITHDRAWN":
				lastActionDate = app.custom_metadata.withdrawn_date
				break;
			default:
				lastActionDate = null;
		}
		app.custom_metadata.lastActionDate = lastActionDate;
	})
}

const reverseSortDirection = (currentSortDirection) => {
	const currentSortDirectionIsDescending = currentSortDirection === 'desc'
	return currentSortDirectionIsDescending ? 'asc' : 'desc';
}

export const myAppsStore = new MyAppsStore();
